
'use client';

import type { Table } from '@tanstack/react-table';
import type { GridConfig } from '../types';

interface TablePaginationProps {
  table: Table<any>;
  gridConfig?: GridConfig;
}

export const TablePagination: React.FC<TablePaginationProps> = ({ table, gridConfig }) => {
  const allPageSizes = gridConfig?.minPageSizes || [10, 20, 50, 100];
  const currentPageSize = table.getState().pagination.pageSize;
  const pageIndex = table.getState().pagination.pageIndex;

  // For manual pagination, use totalRowCount from config if available, otherwise estimate from pageCount
  const isManualPagination = table.options.manualPagination === true;
  let totalRows: number;
  if (isManualPagination) {
    if (gridConfig?.totalRowCount !== undefined) {
      totalRows = gridConfig.totalRowCount;
    } else if (table.options.pageCount) {
      // Estimate: pageCount * pageSize (might be slightly off on last page)
      totalRows = table.options.pageCount * currentPageSize;
    } else {
      totalRows = table.getRowCount();
    }
  } else {
    totalRows = table.getRowCount();
  }

  // Filter page sizes to only include values that make sense for the total row count
  const minPageSize = allPageSizes[0] || 10;

  // Get page sizes <= totalRows, plus one larger option for "show all" capability
  // Also ensure the current page size is always included (important for server-side pagination)
  const filteredPageSizes = allPageSizes.filter(size => size <= totalRows || size === currentPageSize);
  const firstLargerSize = allPageSizes.find(size => size > totalRows && size !== currentPageSize);
  let finalPageSizes = firstLargerSize && filteredPageSizes.length > 0
    ? [...filteredPageSizes, firstLargerSize]
    : filteredPageSizes;

  // Ensure current page size is always in the list and sorted
  if (!finalPageSizes.includes(currentPageSize)) {
    finalPageSizes = [...finalPageSizes, currentPageSize].sort((a, b) => a - b);
  }

  // Determine if we should show the page size selector
  const showPageSizeSelector = totalRows > minPageSize;

  const startRow = totalRows > 0 ? pageIndex * currentPageSize + 1 : 0;
  const endRow = Math.min((pageIndex + 1) * currentPageSize, totalRows);
  
  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-4">
      {/* Row Count Info */}
      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 order-2 sm:order-1">
        <span className="hidden sm:inline">Showing {startRow} to {endRow} of {totalRows} entries</span>
        <span className="sm:hidden">{startRow}-{endRow} of {totalRows}</span>
      </div>

      {/* Controls Container */}
      <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 order-1 sm:order-2 w-full sm:w-auto">
        {/* Page Size Selection - Only show if there are more records than minimum page size */}
        {showPageSizeSelector && finalPageSizes.length > 0 && (
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Show:</span>
            <select
              value={currentPageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              className="px-2 sm:px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {finalPageSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap hidden sm:inline">entries</span>
          </div>
        )}

        {/* Pagination Controls */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* First Page - Hidden on mobile */}
          <button
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            className="hidden sm:flex p-1.5 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 items-center justify-center text-gray-700 dark:text-gray-300"
          >
            <i className="ri-skip-back-line w-4 h-4 flex items-center justify-center"></i>
          </button>

          {/* Previous Page */}
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="p-1.5 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300"
          >
            <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
          </button>

          {/* Page Info */}
          <span className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm whitespace-nowrap text-gray-900 dark:text-gray-100">
            <span className="hidden sm:inline">Page </span>
            {table.getState().pagination.pageIndex + 1}
            <span className="text-gray-400 dark:text-gray-500 mx-1">/</span>
            {table.getPageCount()}
          </span>

          {/* Next Page */}
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="p-1.5 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300"
          >
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          </button>

          {/* Last Page - Hidden on mobile */}
          <button
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            className="hidden sm:flex p-1.5 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 items-center justify-center text-gray-700 dark:text-gray-300"
          >
            <i className="ri-skip-forward-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      </div>
    </div>
  );
};
